import React, { type FC, Fragment, memo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import groupBy from "lodash/groupBy";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import { getExtraCategoriesSelector } from "../../../../../utils/selectorUtils";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import TextInput from "../../../../../../shared-components/css-modules/toyota/molecules/TextInput";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Checkbox from "../../../../../../shared-components/css-modules/toyota/molecules/Checkbox";
import styles from "./SearchBar.module.scss";
import { useDebouncedEffect } from "../../../../../../../../common-deprecated/hooks";
import {
    addCategoryFilter,
    removeCategoryFilter,
    updateSearchValue,
} from "../../../../../redux/slices/CarConfigExtraFilterSlice";
import type { CategoryType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import {
    trackOpenOptionsSearch,
    trackOptionsApplyFilter,
    trackOptionsOpenFilter,
    trackOptionsSearch,
} from "../../../../../utils/tracking";
import { useCarConfigLabel } from "../../../../../utils/labelUtils";
import { addNotification } from "../../../../../../../../common-deprecated/redux/actions/NotificationActions";

const SearchBar: FC<{ extrasActive: boolean }> = ({ extrasActive }): JSX.Element => {
    const dispatch = useCarConfigDispatch();
    const [searchField, setSearchField] = useState<string>("");
    const [searchActive, setSearchActive] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchUpdatedTitle = useCarConfigLabel("carConfigA11ySearchUpdatedTitle");
    const searchUpdatedDescription = useCarConfigLabel("carConfigA11ySearchUpdatedDescription", {
        searchValue: searchField,
    });

    const extraCategories = useCarConfigSelector(getExtraCategoriesSelector, shallowEqual);
    const enabledCategoryFilters = useCarConfigSelector((state) => state.extraFilter.appliedCategoryFilters);

    const clickCategory = (category: CategoryType) => (event: React.ChangeEvent<HTMLInputElement>) => {
        trackOptionsApplyFilter(extrasActive);
        if (event.target.checked) {
            dispatch(addCategoryFilter({ category }));
        } else {
            dispatch(removeCategoryFilter({ category }));
        }
    };

    useDebouncedEffect(
        () => {
            if (searchField) {
                trackOptionsSearch(extrasActive, searchField);
            }
            dispatch(updateSearchValue(searchField));
        },
        500,
        [searchField],
    );

    // Notification only has to be triggered when user has been inactive for 3s
    useDebouncedEffect(
        () => {
            if (searchField) {
                dispatch(
                    addNotification([
                        {
                            title: searchUpdatedTitle,
                            description: searchUpdatedDescription,
                            srOnly: true,
                        },
                    ]),
                );
            }
        },
        3000,
        [searchField],
    );

    const inputLabel = useCarConfigLabel(
        extrasActive ? "carConfigPacksSearchExtrasLabel" : "carConfigPacksSearchPacksLabel",
    );
    const inputPlaceholder = useCarConfigLabel(
        extrasActive ? "carConfigPacksSearchExtrasPlaceholder" : "carConfigPacksSearchPacksPlaceholder",
    );
    const openSearchLabel = useCarConfigLabel("carConfigA11yButtonPacksAndExtrasSearchOpen");
    const closeSearchLabel = useCarConfigLabel("carConfigA11yButtonPacksAndExtrasSearchClose");
    const openFiltersLabel = useCarConfigLabel("carConfigA11yButtonPacksAndExtrasSearchFilters");

    const categorizedExtras = groupBy(extraCategories, "root.name");

    return (
        <>
            <div className={styles["filter-search"]}>
                <Button
                    variant="clean"
                    onClick={() => {
                        if (!searchActive) {
                            trackOpenOptionsSearch(extrasActive);
                        }
                        setSearchActive((value) => !value);
                        inputRef.current?.focus();
                    }}
                    aria-controls="#packs-searchfield"
                    aria-expanded={searchActive}
                    aria-label={openSearchLabel}
                >
                    <Icon variant="search" sizing="md" aria-hidden="true" />
                </Button>
                <Popover placement="bottom-end" onOpenChangeListen={() => trackOptionsOpenFilter(extrasActive)}>
                    <Popover.Trigger>
                        <Button variant="clean" aria-label={openFiltersLabel}>
                            <Icon variant="filter" sizing="md" aria-hidden="true" />
                        </Button>
                    </Popover.Trigger>
                    <Popover.Content variant="filter-box">
                        {Object.entries(categorizedExtras).map(([mainCategory, items]) => (
                            <Fragment key={`filter-box-${mainCategory}`}>
                                <Text
                                    variant="apheleia-body"
                                    spacing="spacing-1"
                                    color="highlight-black"
                                    className={styles["filterbox-category"]}
                                >
                                    {mainCategory}
                                </Text>
                                <div className={styles.filterbox}>
                                    {items.map((category) => (
                                        <Checkbox
                                            key={category.id}
                                            label={category.name}
                                            checked={
                                                !!enabledCategoryFilters.find(
                                                    (enabledFilterCategory) => enabledFilterCategory.id === category.id,
                                                )
                                            }
                                            onChange={clickCategory(category)}
                                        />
                                    ))}
                                </div>
                            </Fragment>
                        ))}
                    </Popover.Content>
                </Popover>
            </div>
            <div
                id="packs-searchfield"
                className={`${styles.searchfield} ${searchActive ? styles.opened : styles.closed}`}
                role="search"
            >
                <TextInput
                    ref={inputRef}
                    label={inputLabel}
                    hideLabel
                    variant="search"
                    placeholder={inputPlaceholder}
                    prefixIcon="search"
                    value={searchField}
                    onChange={(event) => setSearchField(event.target.value)}
                    aria-hidden={!searchActive}
                    tabIndex={!searchActive ? -1 : 0}
                />
                <Button
                    variant="clean"
                    onClick={() => {
                        setSearchActive(false);
                        setSearchField("");
                    }}
                    aria-controls="#packs-searchfield"
                    aria-expanded={searchActive}
                    aria-hidden={!searchActive}
                    tabIndex={!searchActive ? -1 : 0}
                    aria-label={closeSearchLabel}
                    className={styles.close}
                >
                    <Icon variant="close" sizing="md" aria-hidden="true" />
                </Button>
            </div>
        </>
    );
};

export default memo(SearchBar);
